import { useState } from 'react'
import { Bell, Mail, MessageSquare, Calendar, CheckCircle } from 'lucide-react'
import axios from 'axios'
import ModernToggle from '../ui/ModernToggle'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api'

export default function NotificationSettings({ subMenu }) {
  const [emailNotifications, setEmailNotifications] = useState({
    projectUpdates: true,
    teamInvites: true,
    competenceMatches: true,
    weeklyDigest: false,
    newsletter: false
  })

  const [appNotifications, setAppNotifications] = useState({
    projectComments: true,
    mentions: true,
    taskAssignments: true,
    systemAlerts: true,
    achievementUnlocked: true
  })

  const [saveStatus, setSaveStatus] = useState(null)

  const handleEmailToggle = (key) => {
    setEmailNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleAppToggle = (key) => {
    setAppNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleSave = async () => {
    try {
      // TODO: Implémenter l'endpoint backend
      // await axios.put(`${API_URL}/users/me/notifications`, {
      //   email: emailNotifications,
      //   app: appNotifications
      // }, {
      //   headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      // })

      setSaveStatus('success')
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus(null), 3000)
    }
  }

  if (subMenu === 'email') {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="card-header">
            <div>
              <h3 className="card-title">Notifications par email</h3>
              <p className="card-subtitle">Choisissez quels emails vous souhaitez recevoir</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6 space-y-6">
            {/* Mises à jour de projets */}
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Mises à jour de projets</h4>
                  <p className="text-sm text-muted">Notifications sur les changements dans vos projets</p>
                </div>
              </div>
              <ModernToggle
                checked={emailNotifications.projectUpdates}
                onChange={(checked) => setEmailNotifications(prev => ({ ...prev, projectUpdates: checked }))}
              />
            </div>

            <div className="border-t"></div>

            {/* Invitations d'équipe */}
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Mail className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Invitations d'équipe</h4>
                  <p className="text-sm text-muted">Quand vous êtes ajouté à une équipe</p>
                </div>
              </div>
              <ModernToggle
                checked={emailNotifications.teamInvites}
                onChange={(checked) => setEmailNotifications(prev => ({ ...prev, teamInvites: checked }))}
              />
            </div>

            <div className="border-t"></div>

            {/* Correspondances de compétences */}
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Correspondances de compétences</h4>
                  <p className="text-sm text-muted">Nouvelles correspondances RNCP trouvées</p>
                </div>
              </div>
              <ModernToggle
                checked={emailNotifications.competenceMatches}
                onChange={(checked) => setEmailNotifications(prev => ({ ...prev, competenceMatches: checked }))}
              />
            </div>

            <div className="border-t"></div>

            {/* Résumé hebdomadaire */}
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Bell className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Résumé hebdomadaire</h4>
                  <p className="text-sm text-muted">Résumé de votre activité chaque semaine</p>
                </div>
              </div>
              <ModernToggle
                checked={emailNotifications.weeklyDigest}
                onChange={(checked) => setEmailNotifications(prev => ({ ...prev, weeklyDigest: checked }))}
              />
            </div>

            <div className="border-t"></div>

            {/* Newsletter */}
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Newsletter</h4>
                  <p className="text-sm text-muted">Nouveautés produit et conseils</p>
                </div>
              </div>
              <ModernToggle
                checked={emailNotifications.newsletter}
                onChange={(checked) => setEmailNotifications(prev => ({ ...prev, newsletter: checked }))}
              />
            </div>
          </div>

          <div className="border-t p-6">
            <button onClick={handleSave} className="btn btn-primary">
              {saveStatus === 'success' && <CheckCircle className="h-4 w-4" />}
              {saveStatus === 'success' ? 'Enregistré !' : 'Enregistrer les préférences'}
            </button>
            {saveStatus === 'error' && (
              <p className="mt-2 text-sm text-red-600">Erreur lors de la sauvegarde</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (subMenu === 'app') {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="card-header">
            <div>
              <h3 className="card-title">Notifications dans l'application</h3>
              <p className="card-subtitle">Gérez les notifications push et in-app</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6 space-y-6">
            {Object.entries({
              projectComments: {
                icon: MessageSquare,
                color: 'blue',
                title: 'Commentaires sur les projets',
                description: 'Quand quelqu\'un commente un projet que vous suivez'
              },
              mentions: {
                icon: Bell,
                color: 'purple',
                title: 'Mentions',
                description: 'Quand vous êtes mentionné dans un commentaire'
              },
              taskAssignments: {
                icon: CheckCircle,
                color: 'green',
                title: 'Assignations de tâches',
                description: 'Quand une tâche vous est assignée'
              },
              systemAlerts: {
                icon: Bell,
                color: 'red',
                title: 'Alertes système',
                description: 'Notifications importantes du système'
              },
              achievementUnlocked: {
                icon: CheckCircle,
                color: 'yellow',
                title: 'Succès débloqués',
                description: 'Quand vous débloquez un nouveau succès'
              }
            }).map(([key, config], index) => {
              const Icon = config.icon
              return (
                <div key={key}>
                  {index > 0 && <div className="border-t -mt-6 mb-6"></div>}
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 bg-${config.color}-100 rounded-lg flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 text-${config.color}-600`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{config.title}</h4>
                        <p className="text-sm text-muted">{config.description}</p>
                      </div>
                    </div>
                    <ModernToggle
                      checked={appNotifications[key]}
                      onChange={(checked) => setAppNotifications(prev => ({ ...prev, [key]: checked }))}
                    />
                  </div>
                </div>
              )
            })}
          </div>

          <div className="border-t p-6">
            <button onClick={handleSave} className="btn btn-primary">
              {saveStatus === 'success' && <CheckCircle className="h-4 w-4" />}
              {saveStatus === 'success' ? 'Enregistré !' : 'Enregistrer les préférences'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
